<template>
  <div class="container-fluid page-container">
    <div v-if="isChild">
      <router-view />
    </div>
    <div v-else>
      <div class="row">
        <div class="col d-flex page-header__container mb-4 pb-3 justify-content-between">
          <page-heading class="row page-header align-items-center" :title="pageTitle" />
          <result-list-control
            :sortFields="isMobile ? tableFields : null"
            :filter-options="filterOptions"
            :download="exportUrl"
            :show-archive-toggle="true"
            :show-status-key="true"
            :store-key="'myLeads'"
            :default-order="'accepted_date-desc'"
            :disableAll="archiveMode"
            :search="true"
          >
            <template v-slot:icons>
              <my-leads-status-icons v-if="!isMobile" />
            </template>
            <template v-slot:contextual>
              <div v-if="archiveMode" class="filter-item separator archive d-flex justify-content-center align-items-center ml-1 pr-0">
                <b-button
                  variant="outline-primary"
                  class="text-nowrap text-nowrap ml-1 mr-xl-1 btn-sm"
                  size="sm"
                  @click="archive"
                >
                  {{ $t('Archive') }}
                </b-button>
              </div>
            </template>
          </result-list-control>
        </div>
      </div>

      <div v-if="isMobile" class="row">
        <list-accordion>
          <template v-slot:list>
            <my-leads-status-icons :list-container-class="'row row-cols-3 justify-content-center'" />
          </template>
        </list-accordion>
      </div>

      <div class="row">
        <my-leads-list v-if="isMobile" ref="leads" @updated="leadsUpdated" @archive="archiveSelected" />
        <my-leads-table v-else ref="leads" :fields="tableFields" @updated="leadsUpdated" @archive="archiveSelected" />
      </div>
    </div>
  </div>
</template>

<script>
import ResultListControl from "@/components/control/ResultListControl";
import MyLeadsTable from "@/components/table/MyLeadsTable";
import PageHeading from "@/components/control/PageHeading";
import MyLeadsList from "@/components/list/MyLeadsList";
import userDevice from "@/imports/mixins/userDevice";
import MyLeadsStatusIcons from "@/components/list/icons/MyLeadsStatusIcons";
import ListAccordion from "@/components/accordion/ListAccordion";
import company from "@/imports/mixins/company";
import workTypeTranslation from "@/imports/mixins/workTypeTranslation";

export default {
  name: "MyLeads",
  title: 'My Leads',
  components: {MyLeadsList, PageHeading, MyLeadsTable, ResultListControl, MyLeadsStatusIcons, ListAccordion},
  mixins: [userDevice, company, workTypeTranslation],
  data() {
    return {
      pageTitle: this.$t("My Leads"),
      exportUrl: null,
      archiveMode: false,
    }
  },
  computed: {
    isChild: function () {
      return this.$router.currentRoute.name !== 'my-leads'
    },
    tableFields: function() {
      return [
        {key: 'select', label: this.$t('Select All'), canSort: false, class: 'first-col'},
        {key: 'statusIcons', label: this.$t('Status'), canSort: false},
        {key: 'name', label: this.$t('Name'), formatter: 'name', canSort: true, sortable:true, customSortLabels: [this.$t('Surname A-Z'), this.$t('Surname Z-A'),]},
        {key: 'telephone', label: this.$t('Telephone'), formatter: 'telephone', canSort: false},
        {key: 'postcode', label: this.$t('Postcode'), formatter: 'postcode'},
        {
          key: 'workType',
          label: this.$t('Lead Type'),
          formatter: 'workType',
          canSort: true,
          sortable:true,
          customSortLabels: [this.$t('Work Type A-Z'), this.$t('Work Type Z-A')]
        },
        {
          key: 'accepted_date',
          label: this.$t('Date Accepted'),
          canSort: true,
          sortable:true,
          customSortLabels: [this.$t('Newest Lead'), this.$t('Oldest Lead')],
          invertOrder: true
        },
        {
          key: 'timescale',
          label: this.$t('Timescale'),
          formatter: 'timescale',
          canSort: true,
          sortable:true,
          customSortLabels: [this.$t('Least Urgent'), this.$t('Most Urgent')]
        },
        {
          key: 'feedbackRating',
          label: this.$t('Review'),
          formatter: 'rating',
          canSort: true,
          sortable:true,
          customSortLabels: [this.$t('Best Reviews'), this.$t('Lowest Reviews')],
          invertOrder: true
        },
        {key: 'view', label: ''},
      ];
    },
    filterOptions: function() {
      return [
        {
          key: 'timescale',
          label: this.$t('Timescale'),
          values: [{text: this.$t('ASAP'), value: 'ASAP'}, {text: this.$t('1 Month'), value: '1MONTH'}, {
            text: this.$t('3 Months'),
            value: '3MONTHS'
          }]
        },
        {
          key: 'feedbackRating',
          label: this.$t('Feedback'),
          values: [{text: this.$t('Has feedback'), value: 1}, {text: this.$t('No feedback'), value: 0}]
        },
        {key: 'workType', label: this.$t('Lead Type'), values: []},
      ];
    },
  },
  methods: {
    leadsUpdated: function (count) {
      this.exportUrl = (count > 0) ? process.env.VUE_APP_DOWNLOAD_URL + 'lead/my-leads-export' : null;
    },
    loadAvailableWorkTypes: async function () {
      let workTypeRequest = await this.$api.leads.getLeadWorkTypes();
      let options = [];

      workTypeRequest.data.data.forEach((workType) => {
        let category = options.find((optionCategory) => optionCategory.label === workType.category.name);
        if (category === undefined) {
          category = {
            label: workType.category.name,
            children: [],
          }
          options.push(category);
        }
        let subCategory = category.children.find((sub) => sub.real === workType.work_type_category);
        if (subCategory === undefined) {
          subCategory = {
            real: workType.work_type_category,
            label: this.getCategoryTranslation(workType),
            children: [],
          }
          category.children.push(subCategory);
        }
        subCategory.children.push({
          text: this.getDescriptionTranslation(workType, this.company?.communications_language?.iso_code ?? 'en'),
          value: workType.id
        });
      });
      this.filterOptions.find((object) => object.key === 'workType').values = options;
    },
    archiveSelected: function(value) {
      this.archiveMode = value;
    },
    archive: function() {
      this.$refs.leads.archive();
    },
  },
  created: function () {
    this.loadAvailableWorkTypes();
  }
}
</script>

<style scoped lang="scss">
::v-deep {
  .list-control {

    @media only screen and (max-width: 1429px) {
      &__desktop {
        flex-direction: column;

        .list-icons {
          display: flex;
          justify-content: flex-end;
          margin-bottom: .5rem;
          margin-right: 0;

          .separator {
            &:before {
              content: none;
            }
          }
        }
      }
    }

    .archive {
      &:before {
        @media only screen and (max-width: 1399px) {
          content: none;
        }
        @media only screen and (min-width: 1400px) {
          right: -6px;
        }
      }
    }
  }
}

@media only screen and (max-width: 1029px) {
  .separator {
    &:before {
      width: 0;
    }
  }
}
</style>
